import { useEffect, useRef, useState } from 'react';
import { Message } from 'semantic-ui-react';

import * as Constants from '../../Constants/Constant';
import { useAppContext } from '../../Contexts/AppContext';
import { hasGen1AppTab } from '../../Menu/menuConfig';
import Gen1Application from './Application/Gen1Application';
import Gen1History from './History/Gen1History';
import Gen1OrderDetail from './History/Gen1OrderDetail';

/**
 * Gen1 機能のルートコンポーネント。
 *
 * 画面切替の正は Reducer.tabStatus（メニューと同期）とする。
 * - REPAIRREQUEST_STAT_GEN1_APPLICATION → Gen1Application
 * - REPAIRREQUEST_STAT_GEN1_HISTORY     → Gen1History / Gen1OrderDetail
 *
 * メニューに無い Detail・編集対象 ID のみローカル state で保持する。
 * - detailOrderId: History 一覧から開いた注文詳細（0 = 一覧表示）
 * - editOrderId:   Detail から Application へ編集遷移した注文 ID（0 = 新規申請。Application メニュー権限必須）
 */
function scrollToTop() {
    window.scroll({ top: 0 });
}

export default function Gen1() {
    const { Reducer, Actions } = useAppContext();
    const [detailOrderId, setDetailOrderId] = useState(0);
    const [editOrderId, setEditOrderId] = useState(0);
    const prevReducerTabStatus = useRef(Reducer.tabStatus);

    const isGen1Application =
        Reducer.tabStatus === Constants.REPAIRREQUEST_STAT_GEN1_APPLICATION;
    const isGen1History =
        Reducer.tabStatus === Constants.REPAIRREQUEST_STAT_GEN1_HISTORY;

    const canUseGen1Application = hasGen1AppTab(Reducer.userInformation);

    // 画面を離れる（アンマウント）際に、保存エラー表示をクリアする
    useEffect(() => {
        return () => {
            Actions.saveGen1OrderingError(false, '');
        };
    }, []);

    // Application 表示時のみマスタ取得。History のみ権限では API を呼ばない
    // （masterData はサーバー側で Application 権限必須のため）。
    useEffect(() => {
        if (
            canUseGen1Application &&
            isGen1Application &&
            Reducer.csrftoken &&
            !Reducer.gen1MenuPermBlocked
        ) {
            Actions.fetchGen1Masters(Reducer.csrftoken);
        }
    }, [
        canUseGen1Application,
        isGen1Application,
        Reducer.csrftoken,
        Reducer.gen1MenuPermBlocked,
    ]);

    // メニュー等で Reducer.tabStatus が変わったときの後片付け
    useEffect(() => {
        const prev = prevReducerTabStatus.current;
        if (prev !== Reducer.tabStatus) {
            if (
                prev === Constants.REPAIRREQUEST_STAT_GEN1_APPLICATION &&
                Reducer.tabStatus !==
                    Constants.REPAIRREQUEST_STAT_GEN1_APPLICATION
            ) {
                Actions.saveGen1OrderingError(false, '');
            }
            if (
                Reducer.tabStatus ===
                Constants.REPAIRREQUEST_STAT_GEN1_APPLICATION
            ) {
                setDetailOrderId(0);
            } else if (
                Reducer.tabStatus === Constants.REPAIRREQUEST_STAT_GEN1_HISTORY
            ) {
                setEditOrderId(0);
            }
        }
        prevReducerTabStatus.current = Reducer.tabStatus;
    }, [Reducer.tabStatus]);

    /** History 一覧の行クリック。Reducer は HISTORY のまま detailOrderId で Detail を表示する。 */
    function openDetail(orderId) {
        scrollToTop();
        setDetailOrderId(Number(orderId) || 0);
    }

    /** Detail の Back to List。一覧へ戻す（detailOrderId をクリア）。 */
    function backToHistoryList() {
        scrollToTop();
        setDetailOrderId(0);
    }

    /** Detail の Edit。Application タブへ切り替え、対象注文を editOrderId にセットする。 */
    function editFromDetail(orderId) {
        scrollToTop();
        setEditOrderId(Number(orderId) || 0);
        Actions.setTabStatus(Constants.REPAIRREQUEST_STAT_GEN1_APPLICATION);
    }

    /** Application 編集の Cancel。History タブへ戻し、編集していた注文の Detail を再表示する。 */
    function cancelEditToDetail() {
        scrollToTop();
        const id = editOrderId;
        setEditOrderId(0);
        setDetailOrderId(id);
        Actions.setTabStatus(Constants.REPAIRREQUEST_STAT_GEN1_HISTORY);
    }

    if (isGen1History && detailOrderId >= 1) {
        // History のみ権限では Edit を渡さない。editFromDetail は Application タブへ切り替えるが、
        // 当該タブは許可外のため Tab 側ガードで別メニューへリダイレクトされうる。
        return (
            <Gen1OrderDetail
                id={detailOrderId}
                canUseGen1Application={canUseGen1Application}
                onBackToList={backToHistoryList}
                onEditToApplication={
                    canUseGen1Application ? editFromDetail : undefined
                }
            />
        );
    }

    if (isGen1Application) {
        // 権限ブロックは Application のみに適用（History 表示を妨げない）
        if (Reducer.gen1MenuPermBlocked) {
            return (
                <Message warning>
                    <span style={{ whiteSpace: 'pre-line' }}>
                        {Reducer.gen1MenuPermBlockedMessage}
                    </span>
                </Message>
            );
        }
        return (
            <Gen1Application
                editOrderId={editOrderId}
                onCancelToDetail={
                    editOrderId >= 1 ? cancelEditToDetail : undefined
                }
            />
        );
    }

    if (isGen1History) {
        return <Gen1History onOpenDetail={openDetail} />;
    }

    return null;
}
